import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import { convertFileToBase64Model, downloadFile, prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import useColumn from "../../../../utils/customHooks/useColumns";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import DownloadCell from "../../../Components/ProGrid/components/DownloadCell";
import usePageState from "../../../../utils/customHooks/usePageState";
import useDebouncedFilter from "../../../../utils/customHooks/useDebouncedFilter";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { useForm } from "react-hook-form";
import { STAUTES } from "../../../../utils/constant";
import FileUploadField from "../../../Components/Inputs/FileUploadField";
import ProSelectField from "../../../Components/Inputs/SelectField";
import { postApi, getApi, deleteApi, putApi } from "../../../../utils/services";
import { createODataQuery, convertMuiFilterToOData } from "../../../../utils/services/oData";
import { ADMIN_RAP_INFO_DOCUMENTS, RAP_DOCUMENT_TYPES } from "../../../../utils/services/apiPath";
import usePermission from "../../../../utils/customHooks/usePermission";
import { apprenticeRapPermissionSelector } from "../../../store/features/permissions/permissionsSlice";

const RAPDocumentsFormConfing = {
  documentTypeId: {
    label: "Document Type",
    perKey: "SelectDocument",
    rules: { required: "Required!" }
  },
  documents: {
    label: "Select Documents",
    rules: { required: "Documents is required!" }
  }
};

export default function RAPDocuments() {
  const { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(RAPDocumentsFormConfing));
  const { handleSubmit, control, setValue } = useForm({ defaultValues: prepareDefaultValues(RAPDocumentsFormConfing) });
  const readOnly = localStorage.getItem("viewingReadOnlyRapInfo") == "true";
  const [documentTypesData, setDocumentTypesData] = useState([]);
  const [actionData, setActionData] = useState({});
  const [formData, setFormData] = useState();
  const [isAddMode, setIsAddMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { "RAP Documents": rapDocumentPermission } = usePermission(apprenticeRapPermissionSelector, "RAP Documents");
  const { canView, canAdd, canEdit, canDelete, canExport } = rapDocumentPermission || {};

  const {
    currentPage,
    pageSize,
    data: rapDocumentsData,
    rowCount,
    status,
    sortModel,
    searchString,
    filterModel,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString,
    setFilterModel
  } = usePageState();
  const onFilterChange = useDebouncedFilter(setFilterModel);

  const columns = useColumn(
    [
      {
        field: "documentName",
        headerName: "Document Name",
        renderCell: params => <NameCell params={params} onClick={onDocumentClick} />
      },
      {
        field: "rapDocumentType",
        headerName: "Document Type"
      },
      {
        field: "rapDocumentTypeId",
        headerName: "Download",
        renderCell: params => <DownloadCell params={params} onOpen={() => downloadFile(params.row?.logo?.path)} />
      },
      {
        field: "rapDocumentId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} disabled={readOnly} />
      }
    ],
    canDelete
  );

  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const onEdit = () => {
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "edit" } }), {}));
  };
  const handleReadValueClick = name => {
    setFormConfig(prev => ({ ...prev, [name]: { ...prev[name], mode: "edit" } }));
  };

  const resetForm = () => {
    setFormData({});
    setShowGrid(true);
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "read" } }), {}));
  };

  const onCancel = () => {
    resetForm();
  };

  const onDocumentClick = useCallback(async params => {
    setShowGrid(false);
    setActionData(params.row);
    const res = await getApi(`${ADMIN_RAP_INFO_DOCUMENTS}/${params.row.rapDocumentId}`);
    const data = {
      documents: res.documentName,
      documentTypeId: res.rapDocumentTypeId
    };
    setFormData(data);
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
  });

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
  };

  const getAllDocumentTypes = async () => {
    const { data, error } = await getApi(RAP_DOCUMENT_TYPES);
    if (error) return errorToast("Unable to fetch document types. Please try again or contact us.");
    setDocumentTypesData(data?.map(item => ({ label: item.label, value: item.value })));
  };

  const onSave = async data => {
    if (isSaving) return;
    setIsSaving(true);
    const base64ProfilePicture = await convertFileToBase64Model(data.documents);
    const { rapDocumentId } = actionData;
    const payload = {
      rapDocumentTypeId: data?.documentTypeId,
      documentName: data.documents.name,
      rapInfoId: id,
      logo: base64ProfilePicture ?? null
    };
    const methodApi = rapDocumentId ? putApi : postApi;
    const { error } = await methodApi(rapDocumentId ? `${ADMIN_RAP_INFO_DOCUMENTS}/${rapDocumentId}` : ADMIN_RAP_INFO_DOCUMENTS, payload);
    if (error) return errorToast(error);
    successToast(`Partners Documents ${rapDocumentId ? "Updated" : "Added"} successfully.`);
    getDocumentList();
    setIsSaving(false);
    resetForm();
  };

  const onDelete = async id => {
    const { error } = await deleteApi(`${ADMIN_RAP_INFO_DOCUMENTS}/${id}`);
    if (error) return errorToast(error);
    successToast("RAP Document deleted successfully");
    getDocumentList();
  };

  const getDocumentList = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    try {
      const searchableFields = ["documentName", "rapDocumentType"];
      
      // Convert MUI filter model to OData filter
      const gridFilterQuery = convertMuiFilterToOData(filterModel);
      
      // Combine queries including grid filter (excluding rapInfoId as it's a separate param)
      const allQueries = [gridFilterQuery].filter(q => q && q.length > 0);
      
      const { data, totalRecords, error } = await getApi(
        ADMIN_RAP_INFO_DOCUMENTS + createODataQuery(searchableFields, [], allQueries, searchString, sortModel, pageSize, currentPage) + `&rapInfoId=${encodeURIComponent(id)}`
      );
      
      if (error) {
        errorToast(error);
        return;
      }

      setPageState(prevPageInfo => ({
        ...prevPageInfo,
        data:
          data?.map((item, index) => {
            return { ...item, id: index };
          }) || [],
        rowCount: totalRecords
      }));
    } finally {
      setStatus(STAUTES.IDLE);
    }
  }, [currentPage, pageSize, sortModel, searchString, filterModel, id, setPageState, setStatus]);

  useEffect(() => {
    getDocumentList();
    getAllDocumentTypes();
  }, [getDocumentList]);

  const defaultFormProps = { control, formValues: formData, isAddMode, handleReadValueClick, permission: { canView, canAdd, canEdit } };
  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={`${actionData?.rapDocumentsGuid ? "Edit" : "Add"} Document`}
      backAction={() => resetForm()}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      canView={canView}
      backLabel={"Go Back to Documents"}
      hideEdit={readOnly || !canEdit}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title="Documents"
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.rapDocumentId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            filteringMode: "server",
            onSortModelChange: setSortModel,
            onFilterModelChange: onFilterChange
          }}
          columns={columns}
          rows={rapDocumentsData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonLabel="+ Add Document"
          hideExport={!canExport}
          hideAddButton={!canAdd}
          addButtonAction={addButtonAction}
        />
      )}
      {!showGrid && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", marginTop: "5px" }}>
          <FileUploadField {...defaultFormProps} {...formConfig.documents} />
          <ProSelectField {...defaultFormProps} {...formConfig.documentTypeId} options={documentTypesData} />
        </div>
      )}
    </TabGridAndFormLayout>
  );
}
